import * as React from "react"
import { CdnVendor } from "@modules/graphql/types"
import {
  Card,
  CardHeader,
  CardSection,
  Spacer,
  Button,
  ThemeCss,
} from "gatsby-interface"
import { ConnectedDomains, SslCertificates } from "@modules/cdn/gatsbyCloudCdn"
import { useSiteDetailsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { useHostingDetailsForSiteQuery } from "@modules/cdn/gatsbyCloudCdn/queries.generated"
import { AddDomainBlockUp } from "./Domains.parts"
import { useCustomDomainsUsage } from "@modules/organization/shared/hooks/useCustomDomainsUsage"
import { FeatureUsageInfo } from "@modules/site/shared/components/FeatureUsageInfo"
import { SiteSettingsSection, SiteSettingsSectionLabel } from "../constants"

export type DomainsProps = {
  siteId: string
  organizationId: string
}

export function Domains({ siteId, organizationId }: DomainsProps) {
  const { data: siteData } = useSiteDetailsFieldsFragment(siteId)
  const [showForm, setShowForm] = React.useState<boolean>(false)
  const [isBlockUpOpen, setIsBlockupOpen] = React.useState(false)

  const isGatsbyHostingOn =
    siteData?.cdnIntegrations?.find(
      item => item.connected === true && item.vendor === CdnVendor.CloudCdn
    ) || true

  const hasPermissionToEdit = siteData?.permissions?.sites?.edit || undefined

  const { data: hostingData } = useHostingDetailsForSiteQuery({
    variables: { siteId },
    fetchPolicy: `cache-only`,
  })

  const data = hostingData
  const hostingDetails = data?.hostingDetailsForSite?.[0]
  const hastCustomDomain = hostingDetails?.domains?.some(
    item => !item.cloudCdnDefault
  )

  const customDomainsUsage = useCustomDomainsUsage(organizationId)

  const handleClick = () => {
    if (customDomainsUsage.isExhausted && !customDomainsUsage.isUnlimited) {
      setIsBlockupOpen(true)
    } else {
      setShowForm(true)
    }
  }

  if (!isGatsbyHostingOn || !hostingDetails) {
    return null
  }

  return (
    <React.Fragment>
      <Card id={SiteSettingsSection.Domains}>
        <CardHeader
          title={SiteSettingsSectionLabel[SiteSettingsSection.Domains]}
          css={headerCss}
        >
          <div css={headerSuppCss}>
            {!customDomainsUsage.isUnlimited && (
              <React.Fragment>
                <FeatureUsageInfo
                  organizationId={organizationId}
                  message={customDomainsUsage.message}
                  isRecalculating={customDomainsUsage.isRecalculating}
                />
                <Spacer direction="horizontal" size={4} />
              </React.Fragment>
            )}
            {hasPermissionToEdit && (
              <Button variant="PRIMARY" size="M" onClick={handleClick}>
                Add domain
              </Button>
            )}
          </div>
        </CardHeader>
        <CardSection applyPaddingTop={false}>
          {hostingDetails.domains && (
            <ConnectedDomains
              siteId={siteId}
              organizationId={organizationId}
              hostingDetails={hostingDetails}
              allowToEdit={hasPermissionToEdit}
              showForm={showForm}
              onClose={() => setShowForm(false)}
              onSuccess={customDomainsUsage.recalculate}
            />
          )}

          {hostingDetails.certificates && hastCustomDomain && (
            <SslCertificates
              siteId={siteId}
              hostingDetails={hostingDetails}
              allowToEdit={hasPermissionToEdit}
              organizationId={organizationId}
            />
          )}
        </CardSection>
      </Card>

      <AddDomainBlockUp
        organizationId={organizationId}
        isOpen={isBlockUpOpen}
        onDissmis={() => setIsBlockupOpen(false)}
        quantity={customDomainsUsage.used}
      />
    </React.Fragment>
  )
}

/* styles */

const headerCss: ThemeCss = theme => ({
  flexWrap: `wrap`,
})

const headerSuppCss: ThemeCss = _theme => ({
  display: `flex`,
  alignItems: `center`,
})
